/**
 * ELEGIR DOS JUGADORES Y VER SU CARA A CARA.
 *
 * Javier: «si es posible en retos también poder ver el cara a cara donde
 * puedas seleccionar 2 jugadores eso es información publica».
 *
 * POR QUÉ SE PUEDE AHORA. El cara a cara existía solo como «yo contra
 * alguien», y el contrato decía que el de dos terceros «no es una pregunta
 * que la aplicación tenga que contestar». El argumento que lo abrió es que
 * los resultados YA son públicos: el tablón muestra cada partido con los dos
 * nombres y el marcador a cualquiera con cuenta. Contar los que ya se ven no
 * revela nada nuevo.
 *
 * USA LA MISMA BÚSQUEDA QUE «BUSCAR RIVAL» (`searchChallengers`), y no la de
 * administrar un club: esa pide administrar y devuelve el nombre legal, que
 * acá no hace falta ni corresponde.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { ChallengePlayer } from '@tennisup/contracts';

import { Alert } from './Alert';
import { Avatar } from './Avatar';
import { api, assetUrl } from '../lib/api';
import { errorMessage } from '../lib/errorMessage';
import { useAuth } from '../auth/AuthContext';

/**
 * ME AGREGA A MÍ A LOS RESULTADOS, si lo que escribí me calza.
 *
 * POR QUÉ HACE FALTA. Esta pantalla reusa `searchChallengers`, que es la
 * búsqueda de RIVALES PARA RETAR, y ésa excluye a quien busca —nadie se reta
 * a sí mismo—. En el cara a cara eso está mal: «yo contra fulano» es
 * justamente la comparación que la gente quiere ver primero, y Javier lo
 * encontró escribiendo su propio apodo y no apareciendo.
 *
 * SE RESUELVE ACÁ Y NO EN EL SERVIDOR a propósito: la exclusión es correcta
 * para retar y quitarla de la ruta compartida rompería la otra pantalla. Lo
 * que cambia es para qué se busca, y eso lo sabe esta pantalla.
 *
 * COMPARA COMO LO HARÍA UNA PERSONA: sin tildes y sin mayúsculas, porque
 * quien escribe «zurdo» espera encontrar «Zurdo tornado».
 */
function conmigo(
  encontrados: readonly ChallengePlayer[],
  yo: ChallengePlayer | null,
  texto: string,
): ChallengePlayer[] {
  const lista = [...encontrados];
  if (yo === null) return lista;
  // Si el servidor ya me devolvió —no debería, pero por si cambia—, no me duplico.
  /*
   * SI EL SERVIDOR YA ME DEVOLVIÓ, no me duplico.
   *
   * ES DEFENSIVO Y NO TIENE PRUEBA, a propósito: hoy `searchChallengers`
   * excluye siempre a quien busca (`exceptUserId`), así que esta rama no se
   * ejecuta nunca y una prueba tendría que fingir una respuesta que el
   * servidor no da. Queda para el día que esa exclusión cambie — que es
   * justo el día en que aparecería duplicado sin esto.
   */
  if (lista.some((p) => p.userId === yo.userId)) return lista;

  const limpio = (v: string): string =>
    v
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
  const buscado = limpio(texto.trim());
  if (buscado.length === 0) return lista;
  if (!limpio(yo.displayName ?? '').includes(buscado) && !limpio(yo.fullName ?? '').includes(buscado)) {
    return lista;
  }

  // PRIMERO YO, que es a quien más rápido quiere encontrarse quien se busca.
  return [yo, ...lista];
}

/** Desde cuántas letras se sale a buscar. La misma regla que el resto. */
const DESDE = 3;

export function CaraACara(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [uno, setUno] = useState<ChallengePlayer | null>(null);
  const [dos, setDos] = useState<ChallengePlayer | null>(null);

  const listo = uno !== null && dos !== null;

  return (
    <section className="cara-a-cara">
      <div className="section-head">
        <h2>{t('rivalry.pickTitle')}</h2>
      </div>
      <p className="help">{t('rivalry.pickHelp')}</p>

      <Elegir
        id="cara-uno"
        label={t('rivalry.pickFirst')}
        elegido={uno}
        /* NO SE PUEDE ELEGIR DOS VECES A LA MISMA: el servidor lo rechaza
           —«elegí dos jugadores distintos»— y llegar hasta el error para
           enterarse es peor que no poder tocarlo. */
        excluir={dos?.userId ?? null}
        onElegir={setUno}
      />
      <Elegir
        id="cara-dos"
        label={t('rivalry.pickSecond')}
        elegido={dos}
        excluir={uno?.userId ?? null}
        onElegir={setDos}
      />

      <div className="button-row">
        <button
          type="button"
          disabled={!listo}
          onClick={() => navigate(`/jugadores/${uno?.userId}/vs/${dos?.userId}`)}
        >
          {t('rivalry.pickGo')}
        </button>
      </div>
    </section>
  );
}

/**
 * UN CAMPO QUE BUSCA Y DEJA ELEGIR A UNA PERSONA.
 *
 * ELEGIDO SE MUESTRA Y SE PUEDE CAMBIAR. Sin el botón de cambiar, la única
 * forma de corregirse sería recargar la pantalla.
 */
function Elegir({
  id,
  label,
  elegido,
  excluir,
  onElegir,
}: {
  id: string;
  label: string;
  elegido: ChallengePlayer | null;
  excluir: string | null;
  onElegir: (quien: ChallengePlayer | null) => void;
}): JSX.Element {
  const { t } = useTranslation();
  const [texto, setTexto] = useState('');
  const [encontrados, setEncontrados] = useState<ChallengePlayer[]>([]);
  const [error, setError] = useState<string | null>(null);
  const enCurso = useRef<AbortController | null>(null);
  const { session } = useAuth();

  /*
   * YO, CON LA FORMA QUE ESTA LISTA PIDE.
   *
   * La sesión guarda un `AuthUser` —apodo, foto y poco más— y acá hace falta
   * un `ChallengePlayer`. Lo que falta son datos de BÚSQUEDA (distancia,
   * categoría del club, cuántas veces jugamos) y ninguno tiene sentido para
   * uno mismo: la distancia a mí es cero y las veces que jugué conmigo,
   * también. Van en null, que es lo que la fila dibuja como «no se sabe».
   */
  const yo: ChallengePlayer | null =
    session === null
      ? null
      : {
          userId: session.user.id,
          displayName: session.user.displayName,
          fullName: session.user.displayName,
          avatarPath: session.user.avatarPath,
          sex: null,
          locationLabel: '',
          distanceKm: null,
          level: null,
          playedTogether: 0,
          communityName: null,
          declaredLevel: null,
          // VACÍO Y NO MI TELÉFONO: en esta lista yo no soy alguien a quien
          // contactar, soy una opción para comparar. El campo es obligatorio
          // en el tipo, así que va en blanco.
          whatsappPhone: '',
        };

  const buscar = useCallback(
    async (q: string) => {
      // ANTES DE LA TERCERA LETRA NO SE SALE A BUSCAR: con una o dos, la
      // respuesta es media aplicación y no sirve para elegir a nadie.
      if (q.trim().length < DESDE) {
        setEncontrados([]);
        return;
      }
      // Se cancela la anterior: sin esto, la respuesta de «jos» puede llegar
      // después de la de «jose» y pisarla.
      enCurso.current?.abort();
      const propio = new AbortController();
      enCurso.current = propio;
      try {
        setError(null);
        setEncontrados(
          await api.searchChallengers(
            {
              kind: 'FRIENDLY',
              text: q.trim(),
              km: null,
              communityId: null,
              sameCategory: false,
            },
            propio.signal,
          ),
        );
      } catch (caught) {
        // Una búsqueda cancelada no es un error que mostrar: la siguiente ya
        // viene en camino.
        if (propio.signal.aborted) return;
        setError(errorMessage(t, caught));
      }
    },
    [t],
  );

  useEffect(() => {
    const id = setTimeout(() => void buscar(texto), 250);
    return () => clearTimeout(id);
  }, [texto, buscar]);

  if (elegido !== null) {
    return (
      <div className="field">
        <span className="help">{label}</span>
        <p className="cara-elegido">
          <Avatar url={assetUrl(elegido.avatarPath)} name={elegido.displayName} size={32} />
          <strong>{elegido.displayName}</strong>
          <button type="button" className="link" onClick={() => onElegir(null)}>
            {t('common.change')}
          </button>
        </p>
      </div>
    );
  }

  /** Lo que se dibuja: lo que vino del servidor, más yo si me busqué. */
  const resultados = conmigo(encontrados, yo, texto).filter((p) => p.userId !== excluir);

  return (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      <input
        id={id}
        type="search"
        autoComplete="off"
        placeholder={t('rivalry.pickPlaceholder')}
        value={texto}
        onChange={(e) => setTexto(e.target.value)}
      />

      {error !== null && <Alert kind="error">{error}</Alert>}

      {/* LA LISTA SE DIBUJA SI HAY ALGO QUE MOSTRAR, y yo cuento como algo:
          el servidor puede no devolver a nadie y aun así tener que salir yo.
          Antes esto miraba solo `encontrados` y por eso, al buscarse uno
          mismo, no se dibujaba nada. */}
      {resultados.length > 0 && (
        <ul className="plain-list cara-resultados">
          {resultados.map((p) => (
              <li key={p.userId}>
                <button type="button" className="link" onClick={() => onElegir(p)}>
                  <Avatar url={assetUrl(p.avatarPath)} name={p.displayName} size={28} />
                  {p.displayName}
                </button>
              </li>
            ))}
        </ul>
      )}
    </div>
  );
}
